/**
 * Covers or list, as a two-position switch.
 *
 * A segmented control rather than two loose buttons: the choice is exclusive
 * and there are exactly two of them, so the control should say so by holding
 * them in one frame with one of the two lit. Icons only, at every width: the
 * words "covers" and "list" would take a third of the line on a phone to name
 * something the icons already draw.
 */

import { t, useUiLang } from "../i18n";
import type { ViewMode } from "./useLibrary";

function GridIcon() {
  return (
    <svg
      viewBox="0 0 20 20"
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="3" width="6" height="6" rx="1" />
      <rect x="11" y="3" width="6" height="6" rx="1" />
      <rect x="3" y="11" width="6" height="6" rx="1" />
      <rect x="11" y="11" width="6" height="6" rx="1" />
    </svg>
  );
}

function ListIcon() {
  return (
    <svg
      viewBox="0 0 20 20"
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <path d="M3 5.5h14M3 10h14M3 14.5h14" />
    </svg>
  );
}

export function ViewToggle({
  view,
  onChange,
}: {
  view: ViewMode;
  onChange: (next: ViewMode) => void;
}) {
  useUiLang();
  const options: { mode: ViewMode; label: string; icon: React.ReactNode }[] = [
    { mode: "grid", label: t("viewGrid"), icon: <GridIcon /> },
    { mode: "list", label: t("viewList"), icon: <ListIcon /> },
  ];

  return (
    <div
      role="group"
      aria-label={t("viewLabel")}
      className="inline-flex shrink-0 items-center gap-0.5 rounded-lg border border-[var(--color-line)] p-0.5"
    >
      {options.map((option) => {
        const active = view === option.mode;
        return (
          <button
            key={option.mode}
            onClick={() => onChange(option.mode)}
            aria-pressed={active}
            title={option.label}
            aria-label={option.label}
            className={
              // `tap` carries the 44px target under a thumb without the visible
              // box growing, which is what keeps the header on one line.
              "tap inline-flex h-7 w-8 items-center justify-center rounded-md transition-colors duration-150 " +
              (active
                ? "bg-[var(--color-accent-soft)] text-[var(--color-accent)]"
                : "text-[var(--color-faint)] hover:text-[var(--color-ink)]")
            }
          >
            {option.icon}
          </button>
        );
      })}
    </div>
  );
}
